import { type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Collapse, Stack, styled } from "~/common/mui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Add, ExpandLess } from "@mui/icons-material";
import type { ProductLineViewModel } from "~/features/cart/cartV2/models/ProductTableViewModel";
import { ConnectedEntriesChain } from "~/features/cart/cartV2/ConnectedEntriesChain";

interface CartEntryDetailsProps {
  initiallyExpanded?: boolean;
  showCopyLine: boolean;
  canCopyLine: boolean;
  copySourcePackEntryId: number | null;
  lines: ProductLineViewModel[];
  intraEntryChainInsertAfterLineIds?: number[];
  renderItem: (line: ProductLineViewModel) => ReactNode;
  editMode: boolean;
  onCopyLine: (packEntryId: number) => void;
}

export const CartEntryDetails = ({
  initiallyExpanded,
  showCopyLine,
  canCopyLine,
  copySourcePackEntryId,
  lines,
  intraEntryChainInsertAfterLineIds,
  renderItem,
  editMode,
  onCopyLine,
}: CartEntryDetailsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(initiallyExpanded ?? false);
  const chainAfterIds = useMemo(
    () => new Set(intraEntryChainInsertAfterLineIds ?? []),
    [intraEntryChainInsertAfterLineIds],
  );

  return (
    <DetailsRoot $expanded={expanded}>
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        <Button
          variant="text"
          startIcon={expanded ? <ExpandLess /> : <ExpandMoreIcon />}
          color="textPrimary"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {`${expanded ? t("common.hide") : t("common.show")} ${t("cartPage.summary.productLines")}`}
        </Button>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1 }}>
          <Stack spacing={2}>
            {lines.flatMap((line) => {
              const items = [renderItem(line)];
              if (chainAfterIds.has(line.lineItemId)) {
                items.push(
                  <Box
                    key={`chain-after-${line.lineItemId}`}
                    sx={{ display: "flex", justifyContent: "center", marginBlock: "-17px" }}
                  >
                    <ConnectedEntriesChain />
                  </Box>,
                );
              }
              return items;
            })}
          </Stack>
        </Box>
        {editMode && showCopyLine && (
          <Box sx={{ p: 2 }}>
            <Button
              data-test-id="copy-line-button"
              onClick={() => {
                if (copySourcePackEntryId !== null) {
                  onCopyLine(copySourcePackEntryId);
                }
              }}
              startIcon={<Add />}
              variant="outlined"
              disabled={!canCopyLine || copySourcePackEntryId === null}
            >
              {t("cartPage.table.copyRow")}
            </Button>
          </Box>
        )}
      </Collapse>
    </DetailsRoot>
  );
};

const DetailsRoot = styled(Box)<{ $expanded: boolean }>(({ theme, $expanded }) => ({
  background: $expanded ? theme.palette.surfaceContainerLow?.main : theme.palette.common.white,
  transition: `background ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
}));
